/**
 * Product card variants.
 *
 * `card-swatches` scrolls the strip of values and its two arrows. `card-quick-add` re-renders the options of the panel
 * when a swatch changes, from the JSON payload next to it, and adds the variant carried by the pressed control — the
 * submission itself is left to the theme `product-form`, so the cart drawer, the cart count and everything listening to
 * `variant:add` keep working as they do on the product page.
 */

const NOTICE_DURATION = 4000; // A sold out or failed pick says so, then gets out of the way
const BUSY_TIMEOUT = 10000; // Safety net: a request that never resolves must not leave a button spinning forever

/** Scrolls by a page. In RTL the scroll offset grows towards the negative, so "forward" is a negative delta. */
function scrollByPage(element, direction) {
  const forward = getComputedStyle(element).direction === 'rtl' ? -1 : 1,
    amount = Math.max(element.clientWidth * 0.8, 80);

  element.scrollBy({ left: forward * amount * (direction === 'prev' ? -1 : 1), behavior: 'smooth' });
}

class CardSwatches extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const { signal } = this._abortController;

    this.addEventListener('click', this._onClick.bind(this), { signal });
    this.track?.addEventListener('scroll', () => this._updateNavButtons(), { passive: true, signal });

    // Deferred to the viewport: a listing has hundreds of strips and measuring them all on load costs a layout pass
    if ('IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._intersectionObserver.disconnect();
          this._measure();
        }
      });

      this._intersectionObserver.observe(this);
    } else {
      this._measure();
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._intersectionObserver?.disconnect();
    this._resizeObserver?.disconnect();
  }

  get track() {
    return this.querySelector('.card-swatches__track');
  }

  _onClick(event) {
    const navButton = event.target.closest('[data-scroll-direction]');

    if (navButton && this.track) {
      scrollByPage(this.track, navButton.getAttribute('data-scroll-direction'));
    }
  }

  _measure() {
    const track = this.track;

    if (!track) {
      return;
    }

    this._revealSelected();

    if ('ResizeObserver' in window) {
      // Every width change can push the selected swatch out of the visible part of the strip — a phone being rotated,
      // a responsive preview being resized, or simply the layout settling after the first paint. It is therefore
      // brought back on each of them, not only once.
      this._resizeObserver = new ResizeObserver(() => {
        this._revealSelected();
        this._updateNavButtons();
      });

      this._resizeObserver.observe(track);
    } else {
      this._updateNavButtons();
    }
  }

  /** Brings the selected swatch into view, by hand: scrollIntoView would also scroll the page to this card. */
  _revealSelected() {
    const track = this.track,
      checked = track?.querySelector('[type="radio"]:checked');

    if (!checked) {
      return;
    }

    // The radio is the SIBLING of the swatch, never its parent
    const swatch =
      (checked.id && track.querySelector(`label[for="${CSS.escape(checked.id)}"]`)) || checked.nextElementSibling;

    if (!swatch) {
      return;
    }

    const styles = getComputedStyle(track),
      trackRect = track.getBoundingClientRect(),
      swatchRect = swatch.getBoundingClientRect();

    // Measured against the CONTENT box: its padding is the room the ring, drawn outside the swatch, needs
    const contentLeft = trackRect.left + (parseFloat(styles.paddingLeft) || 0),
      contentRight = trackRect.right - (parseFloat(styles.paddingRight) || 0);

    if (swatchRect.left >= contentLeft - 0.5 && swatchRect.right <= contentRight + 0.5) {
      return;
    }

    // Aligned on the start edge of the strip, which is the right one in a right to left document
    track.scrollLeft +=
      styles.direction === 'rtl' ? swatchRect.right - contentRight : swatchRect.left - contentLeft;
  }

  /** The arrows only show when there is something to scroll to. */
  _updateNavButtons() {
    const track = this.track;

    if (!track) {
      return;
    }

    const offset = Math.abs(track.scrollLeft),
      maxOffset = track.scrollWidth - track.clientWidth,
      isScrollable = maxOffset > 1;

    this.querySelectorAll('[data-scroll-direction]').forEach((button) => {
      const isPrevious = button.getAttribute('data-scroll-direction') === 'prev';

      button.hidden = !isScrollable;
      button.disabled = isPrevious ? offset <= 1 : offset >= maxOffset - 1;
    });
  }
}

class CardQuickAdd extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const { signal } = this._abortController;

    this.addEventListener('click', this._onClick.bind(this), { signal });

    // Dispatched by the theme `product-form` and bubbling up to here
    this.addEventListener('variant:add', this._onVariantAdded.bind(this), { signal });
    this.addEventListener('cart:error', this._onCartError.bind(this), { signal });

    // The swatches live outside this element, in the info of the card
    this.card?.addEventListener('change', this._onSwatchChanged.bind(this), { signal });
    this.card?.addEventListener('click', this._onCardClick.bind(this), { signal });

    this._onDocumentClickListener = this._onDocumentClick.bind(this);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._setOpen(false);

    clearTimeout(this._busyTimeout);
    clearTimeout(this._noticeTimeout);
  }

  get card() {
    return this.closest('product-card');
  }

  get form() {
    return this.querySelector('form');
  }

  get variantInput() {
    return this.querySelector('[data-quick-add-variant-input]');
  }

  get optionsElement() {
    return this.querySelector('[data-quick-add-options]');
  }

  get feedbackElement() {
    return this.querySelector('[data-quick-add-feedback]');
  }

  /** Variants of every option value, keyed by value name. A listing split by variant renders it once per product. */
  get payload() {
    if (this._payload === undefined) {
      const handle = this.getAttribute('handle');

      const payloadElement =
        this.querySelector('[data-quick-add-payload]') ||
        (handle
          ? document.querySelector(`card-quick-add[handle="${CSS.escape(handle)}"] [data-quick-add-payload]`)
          : null);

      try {
        this._payload = payloadElement ? JSON.parse(payloadElement.textContent) : null;
      } catch (error) {
        this._payload = null; // A malformed payload must never break the card, it just stops switching variant
      }
    }

    return this._payload;
  }

  // -------------------------------------------------------------------------------------------------------------------
  // SWITCHING VARIANT
  // -------------------------------------------------------------------------------------------------------------------

  _onSwatchChanged(event) {
    const input = event.target;

    if (!input.matches?.('.card-swatches__track [type="radio"]')) {
      return;
    }

    const valueName = input.getAttribute('data-option-value-name'),
      entry = valueName ? this.payload?.[valueName] : null;

    if (!entry) {
      return;
    }

    this._renderOptions(entry.v || []);
    this._renderPrice(entry.p);
  }

  /** Rebuilds the options of the newly selected value, cloning the markup Liquid rendered. */
  _renderOptions(variants) {
    this._setSoldOut(!variants.some(([, , available]) => available));

    const fallback = variants.find(([, , available]) => available) || variants[0];

    if (fallback && this.variantInput) {
      this.variantInput.value = String(fallback[0]);
    }

    const optionsElement = this.optionsElement;

    if (!optionsElement) {
      this._renderAddButton(variants);
      return;
    }

    const byLabel = new Map(variants.map(([id, label, available]) => [label, [id, available]])),
      soldOutLabel = this.soldOutLabel,
      productTitle = this.card?.querySelector('.product-card__title')?.textContent.trim() || '';

    optionsElement.querySelectorAll('.card-quick-add__option').forEach((button) => {
      const label = button.getAttribute('data-option-label'),
        [id, available] = byLabel.get(label) || [null, false];

      if (id === null) {
        button.removeAttribute('data-variant-id');
      } else {
        button.setAttribute('data-variant-id', String(id));
      }

      button.classList.toggle('is-disabled', !available);
      button.toggleAttribute('data-sold-out', !available);
      button.removeAttribute('aria-busy');
      button.querySelector('.card-quick-add__option-loader')?.remove();
      button.setAttribute('aria-label', `${productTitle} - ${label}${available ? '' : ` (${soldOutLabel})`}`);
    });
  }

  /** The add buttons of a product with nothing to pick: they follow the swatch, and go when the value is sold out. */
  _renderAddButton(variants) {
    const addable = variants.find((variant) => variant[2]),
      buttons = [
        this.querySelector('[data-quick-add-button]'),
        this.card?.querySelector('[data-quick-add-direct]')
      ];

    buttons.forEach((button) => {
      if (!button) {
        return;
      }

      button.removeAttribute('aria-busy');
      button.hidden = !addable;

      if (addable) {
        button.setAttribute('data-variant-id', String(addable[0]));
      } else {
        button.removeAttribute('data-variant-id');
      }
    });
  }

  _renderPrice(priceHtml) {
    if (!priceHtml) {
      return; // Prices do not vary, the rendered one is already right
    }

    const priceElement = this.card?.querySelector('.product-card__info price-list');

    if (priceElement) {
      priceElement.outerHTML = priceHtml;
    }
  }

  _setSoldOut(isSoldOut) {
    const badge = this.card?.querySelector('sold-out-badge');

    if (badge) {
      badge.hidden = !isSoldOut;
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // ADDING TO CART
  // -------------------------------------------------------------------------------------------------------------------

  get sheet() {
    return document.getElementById('card-size-drawer');
  }

  /** True while the sheet on screen is the one this card filled, so no card ever closes another card's sheet. */
  get ownsSheet() {
    const sheet = this.sheet;

    return Boolean(sheet && sheet.cardQuickAdd === this && sheet.open);
  }

  /**
   * A touch device has no hover, so the options move into the bottom sheet of the page. There is one sheet for the
   * whole listing: it is filled with the options of the card that opened it, and hands their clicks back to that card.
   */
  _openSheet(toggle) {
    const sheet = this.sheet,
      list = sheet?.querySelector('[data-card-size-list]'),
      options = this.optionsElement;

    if (!sheet || !list || !options) {
      return;
    }

    sheet.querySelector('[data-card-size-title]').textContent = toggle.getAttribute('data-sheet-title') || '';
    list.replaceChildren(...Array.from(options.children).map((option) => option.cloneNode(true)));

    sheet.cardQuickAdd = this;
    sheet.show();
  }

  /** A click on an option of the sheet is an add on the card that opened it. */
  _onSheetClick(event) {
    const option = event.target.closest('button.card-quick-add__option');

    if (!option) {
      return;
    }

    if (option.hasAttribute('data-sold-out')) {
      this._setNotice(this.soldOutLabel);
      return;
    }

    this._addToCart(option);
  }

  /** A touch device has no hover: the panel is toggled by the quick add button, which sits outside this element. */
  _onCardClick(event) {
    const toggle = event.target.closest('[data-quick-add-toggle]');

    if (toggle) {
      this._openSheet(toggle);
      return;
    }

    // Same button, same corner, on a product with nothing to pick: it adds instead of opening
    const directButton = event.target.closest('[data-quick-add-direct]');

    if (directButton) {
      this._addToCart(directButton);
      return;
    }

    // Anywhere else on the card closes it again — except the swatches, so a color can be picked with it open
    if (this.hasAttribute('open') && !this.contains(event.target) && !event.target.closest('card-swatches')) {
      this._setOpen(false);
    }
  }

  /** Capture phase, so the very click that opened the panel — already past it — never closes it again. */
  _onDocumentClick(event) {
    if (!this.card?.contains(event.target)) {
      this._setOpen(false);
    }
  }

  _setOpen(isOpen) {
    if (isOpen === this.hasAttribute('open')) {
      return;
    }

    this.toggleAttribute('open', isOpen);
    this.card?.classList.toggle('product-card--quick-add-open', isOpen);
    this.card?.querySelector('[data-quick-add-toggle]')?.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      document.addEventListener('click', this._onDocumentClickListener, true);
    } else {
      document.removeEventListener('click', this._onDocumentClickListener, true);
    }
  }

  get soldOutLabel() {
    return this.getAttribute('data-sold-out-label') || '';
  }

  /** The card has no add to cart button to carry a "sold out" state, so it is reported on this line instead. */
  _setNotice(message) {
    clearTimeout(this._noticeTimeout);

    const targets = [this.feedbackElement];

    if (this.ownsSheet) {
      targets.push(this.sheet.querySelector('[data-card-size-notice]'));
    }

    targets.forEach((element) => {
      if (element) {
        element.textContent = message;
        element.hidden = !message;
      }
    });

    if (message) {
      this._noticeTimeout = setTimeout(() => this._setNotice(''), NOTICE_DURATION);
    }
  }

  _swatchOf(input) {
    return (input.id && this.card?.querySelector(`label[for="${CSS.escape(input.id)}"]`)) || input.nextElementSibling;
  }

  _onClick(event) {
    const option = event.target.closest('button.card-quick-add__option');

    if (option) {
      if (option.hasAttribute('data-sold-out')) {
        this._setNotice(this.soldOutLabel);
      } else {
        this._setNotice('');
        this._addToCart(option);
      }

      // A press with the mouse hands the focus back, so :focus-within cannot hold the panel open once the pointer goes
      if (event.detail > 0) {
        option.blur();
      }

      return;
    }

    // The swatch inputs carry a "data-variant-id" too, for the media swap, hence the button
    this._addToCart(event.target.closest('button[data-variant-id]'));
  }

  _addToCart(button, resolvedVariantId) {
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const form = this.form,
      variantInput = this.variantInput,
      variantId = resolvedVariantId || button.getAttribute('data-variant-id');

    if (!form || !variantInput || !variantId) {
      return;
    }

    // The single source of truth for what is added
    variantInput.value = variantId;

    this._setBusy(button);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      // Safari < 16 does not implement requestSubmit, the `product-form` element listens for the event anyway
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /** `custom-button` draws its own loader; an option button gets one injected rather than every option carrying one. */
  _setBusy(button) {
    this._clearBusy();
    this._busyButton = button;

    button.setAttribute('aria-busy', 'true');

    if (button.classList.contains('card-quick-add__option') && !button.querySelector('.card-quick-add__option-loader')) {
      const loader = document.createElement('span');

      loader.className = 'card-quick-add__option-loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));

      button.appendChild(loader);
    }

    this._busyTimeout = setTimeout(() => this._clearBusy(), BUSY_TIMEOUT);
  }

  _clearBusy() {
    clearTimeout(this._busyTimeout);

    this._busyButton?.removeAttribute('aria-busy');
    this._busyButton?.querySelector('.card-quick-add__option-loader')?.remove();
    this._busyButton = null;
  }

  /** Nothing on success: the cart notification already confirms the add, in front of the card. */
  _onVariantAdded() {
    // Closed only now, not on the press: the sheet carries the loader while the request is in flight and hands over to
    // the cart notification
    if (this.ownsSheet) {
      this.sheet.hide();
    }

    this._clearBusy();

    this._setOpen(false);

    this._setNotice('');
  }

  /** A failure has nowhere else to surface on a listing, so it stays on the card until the visitor tries again. */
  _onCartError(event) {
    this._clearBusy();

    const feedbackElement = this.feedbackElement,
      message = event.detail?.error;

    if (!feedbackElement || !message) {
      return;
    }

    feedbackElement.textContent = message;
    feedbackElement.hidden = false;
  }
}

// The sheet is shared, so its clicks are routed to the card that last filled it
document.addEventListener('click', (event) => {
  const sheet = event.target.closest?.('#card-size-drawer');

  sheet?.cardQuickAdd?._onSheetClick(event);
});

if (!window.customElements.get('card-swatches')) {
  window.customElements.define('card-swatches', CardSwatches);
}

if (!window.customElements.get('card-quick-add')) {
  window.customElements.define('card-quick-add', CardQuickAdd);
}
